import { yupResolver } from '@hookform/resolvers/yup'
import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import Button from '../../../../components/Button'
import { Column, Container, Form, Row } from '../../../../components/commons'
import Input from '../../../../components/Input'
import Modal from '../../../../components/Modals/Modal'
import useToast from '../../../../hooks/useToast'
import { api } from '../../../../services/api'

import {
  DocumentTypeForm,
  registerDocumentTypeSchema,
} from '../../../../validators/document.schemas'
import MetaDataInput from './MetaDataInput'
import { GroupItem } from '../../Documents/FileDetails/GroupsSearch'
import { Group } from '../../../../services/groups.service'
import SearchGroup from '../../Documents/FileDetails/GroupsSearch/SearchGroup'
import { SideBarStyles } from '../../Logs/SideBarLogs/styles'

export interface CreateProps {
  reload: () => void
}

export interface ModalHandle {
  openFormModal: (initialData?: DocumentTypeForm) => void
}

type ModalRef = React.ElementRef<typeof Modal>

const CreateType: React.ForwardRefRenderFunction<ModalHandle, CreateProps> = ({ reload }, ref) => {
  const [loading, setLoading] = useState(false)

  const [groups, setGroups] = useState<Group[]>([])

  const [initialData, setInitialData] = useState<DocumentTypeForm>({
    name: '',
    metadata: [],
  })

  const toast = useToast()

  const modalRef = useRef<ModalRef>(null)

  const handleOpenModalCreate = () => {
    modalRef.current?.openModal()
  }

  const {
    setValue,
    control,
    handleSubmit,
    reset,
    getValues,
    setError,
    watch,
    formState: { errors },
  } = useForm<DocumentTypeForm>({
    defaultValues: initialData,
    resolver: yupResolver(registerDocumentTypeSchema),
  })

  const handleSubmitLogin = async (formData: any) => {
    try {
      setLoading(true)

      if (formData.metadata) {
        formData.metadata = formData.metadata?.filter((met: any) => met.name !== '')
      }

      formData.groups = groups.map((group) => group?._id)

      formData.classificationCode = ttdData.classificationCode
      formData.currentPhaseYears = Number(ttdData.currentPhaseYears) || 0
      formData.intermediatePhaseYears = Number(ttdData.intermediatePhaseYears) || 0
      formData.finalDestination = ttdData.finalDestination
      formData.legalBasis = ttdData.legalBasis

      if (initialData?._id) {
        await api.put(`/documentTypes/${initialData?._id}`, {
          ...formData,
        })
      } else {
        await api.post('/documentTypes/', formData)
      }

      //modalRef.current?.closeModal()
      reload()
      reset()

      if (initialData?._id) {
        toast.successToast('Tipo de Documento criado com sucesso !')
      } else {
        toast.successToast('Tipo de Documento Atualizado com sucesso !')
      }
    } catch (error) {
      toast.errorToast('Erro ao criar Tipo de Documento !')
    } finally {
      setLoading(false)
    }
  }

  const [ttdData, setTtdData] = useState({
    classificationCode: '000.0',
    currentPhaseYears: 1,
    intermediatePhaseYears: 5,
    finalDestination: 'permanente',
    legalBasis: '',
  })

  React.useImperativeHandle(ref, () => ({
    openFormModal(initialData: any) {
      reset()
      if (initialData) {
        setInitialData(initialData)
        setValue('name', initialData.name)
        setValue('metadata', initialData?.metadata)
        setTtdData({
          classificationCode: initialData?.classificationCode || '000.0',
          currentPhaseYears: initialData?.currentPhaseYears ?? 1,
          intermediatePhaseYears: initialData?.intermediatePhaseYears ?? 5,
          finalDestination: initialData?.finalDestination || 'permanente',
          legalBasis: initialData?.legalBasis || '',
        })
        setGroups(
          initialData?.groups?.map((group: any) => ({
            name: group.name,
            _id: group._id,
          })),
        )
      } else {
        setInitialData({
          name: '',
          metadata: [],
        })
        setTtdData({
          classificationCode: '000.0',
          currentPhaseYears: 1,
          intermediatePhaseYears: 5,
          finalDestination: 'permanente',
          legalBasis: '',
        })
        reset()
        setGroups([])
      }
      handleOpenModalCreate()
    },
  }))

  const onChangeSelect = async (itemId: string, reset: any, itemSelected: Group) => {
    const findUser = groups.find((group) => group._id === itemId)
    if (findUser) {
      return toast.errorToast('Usuario ja selecionado')
    }
    const userAux = [...groups]

    userAux.push({
      _id: itemSelected?._id,
      name: itemSelected?.name,
      users: [],
    })

    reset()
    setGroups([...userAux])

    if (initialData?._id) {
      await api.put(`/documentTypes/${initialData?._id}/groups/${itemId}`)

      toast.successToast('Grupo adicionado com sucesso !')
    }
  }

  const handleDelete = async (itemId: string) => {
    const userAux = groups.filter((user) => user._id !== itemId)

    setGroups([...userAux])

    if (initialData?._id) {
      await api.delete(`/documentTypes/${initialData?._id}/groups/${itemId}`)
      toast.successToast('Usuario removido com sucesso !')
    }
  }

  return (
    <>
      <Modal title='Cadastrar novo tipo' ref={modalRef}>
        <Container>
          <Form onSubmit={handleSubmit(handleSubmitLogin)}>
            <Input
              label='Nome'
              placeholder='Digite o nome do tipo'
              error={errors?.name}
              control={control}
              type='text'
              name='name'
            />

            <MetaDataInput
              label='Campos'
              getValues={getValues}
              setError={setError}
              error={errors.metadata}
              control={control}
              name='metadata'
            />

            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                  ⚖️ Tabela de Temporalidade Documental (TTD)
                </span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Os prazos de guarda (fases corrente e intermediária) e a destinação final são configurados no botão específico <strong>"⚖️ Regras TTD"</strong> na listagem.
                </span>
              </div>
            </div>

            <Column style={{ gap: 15 }}>
              <Row justify='space-between'>
                <SideBarStyles.Title>Grupos Permitidos</SideBarStyles.Title>
              </Row>
              <SearchGroup
                menuPosition={'fixed'}
                menuPortalTarget={document.querySelector('#portalDocumentTypeModal')}
                loadingSubmit={loading}
                onSelectGroup={onChangeSelect}
              />
              {groups.map((group) => (
                <GroupItem item={group} handleSubmitDelete={handleDelete} />
              ))}
            </Column>
            <Button
              label='Salvar'
              loading={loading}
              type='submit'
              variantType='block'
              color='primary'
            />
          </Form>
        </Container>
      </Modal>
    </>
  )
}

export default React.forwardRef(CreateType)
